import SEO from '../Components/SEO';
import React from "react";
import {
  Info,
  Layers,
  MessageSquare,
  ShoppingBag,
  DollarSign,
  Truck,
  Award,
  RefreshCw,
  FileText,
  Copyright,
  ExternalLink,
  UserCheck,
  Scale,
  Edit3,
  Globe,
  Phone
} from "lucide-react";

const TermsAndConditions = () => {
  const brandColor = "#fc7303";

  const sections = [
    { id: "about", label: "About Our Website", icon: <Info size={20} color={brandColor} /> },
    { id: "products", label: "Our Products", icon: <Layers size={20} color={brandColor} /> },
    { id: "enquiries", label: "Product Enquiries", icon: <MessageSquare size={20} color={brandColor} /> },
    { id: "purchases", label: "Offline Purchases", icon: <ShoppingBag size={20} color={brandColor} /> },
    { id: "pricing", label: "Pricing & Availability", icon: <DollarSign size={20} color={brandColor} /> },
    { id: "delivery", label: "Delivery & Installation", icon: <Truck size={20} color={brandColor} /> },
    { id: "warranty", label: "Product Warranty", icon: <Award size={20} color={brandColor} /> },
    { id: "returns", label: "Returns & Exchanges", icon: <RefreshCw size={20} color={brandColor} /> },
    { id: "content", label: "Website Content", icon: <FileText size={20} color={brandColor} /> },
    { id: "intellectual", label: "Intellectual Property", icon: <Copyright size={20} color={brandColor} /> },
    { id: "links", label: "Third-Party Links", icon: <ExternalLink size={20} color={brandColor} /> },
    { id: "user", label: "User Responsibilities", icon: <UserCheck size={20} color={brandColor} /> },
    { id: "liability", label: "Limitation of Liability", icon: <Scale size={20} color={brandColor} /> },
    { id: "changes", label: "Changes to These Terms", icon: <Edit3 size={20} color={brandColor} /> },
    { id: "law", label: "Governing Law", icon: <Globe size={20} color={brandColor} /> },
    { id: "contact", label: "Contact Us", icon: <Phone size={20} color={brandColor} /> },
  ];

  return (
    <div
      style={{
        maxWidth: "1200px",
      }}>
      <SEO
        title="Terms and Conditions | Sree Raghavendra Doors & Hardware"
        description="Terms and conditions for product purchases, orders, offline warranties, and website terms for Sree Raghavendra Doors, Plywood & Hardware."
        canonicalUrl="/terms-and-conditions"
      />

        margin: "0 auto",
        padding: "60px 24px",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <style>{`
        .policy-layout {
          display: flex;
          gap: 40px;
          align-items: flex-start;
        }
        
        .policy-sidebar {
          width: 300px;
          position: sticky;
          top: 100px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          max-height: calc(100vh - 140px);
          overflow-y: auto;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);
        }
        
        .policy-sidebar-title {
          font-family: 'Oswald', sans-serif;
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #64748b;
          margin-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 8px;
        }
        
        .policy-sidebar-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .policy-sidebar-link {
          color: #475569;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .policy-sidebar-link:hover {
          color: ${brandColor};
          transform: translateX(4px);
        }
        
        .policy-content {
          flex: 1;
          color: #334155;
          line-height: 1.8;
        }
        
        .policy-section {
          scroll-margin-top: 110px;
          margin-bottom: 40px;
        }

        .policy-section h3 {
          font-family: 'Oswald', sans-serif;
          color: #0f172a;
          font-size: 1.4rem;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 8px;
        }
        
        @media (max-width: 991px) {
          .policy-layout {
            flex-direction: column;
            gap: 20px;
          }
          
          .policy-sidebar {
            width: 100%;
            position: relative;
            top: 0;
            max-height: none;
            margin-bottom: 20px;
            padding: 20px;
          }
        }
      `}</style>

      <div className="policy-layout">
        {/* SIDEBAR TABLE OF CONTENTS */}
        <aside className="policy-sidebar">
          <div className="policy-sidebar-title">On This Page</div>
          <ul className="policy-sidebar-list">
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="policy-sidebar-link">
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* POLICY CONTENT */}
        <main className="policy-content">
          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              color: brandColor,
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "2.5rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Terms & Conditions
          </h1>
          <p style={{ color: "#64748b", fontSize: "0.95rem", marginBottom: "40px" }}>
            Last Updated: August 2026
          </p>

          <p style={{ fontSize: "1.1rem", marginBottom: "45px", color: "#1e293b" }}>
            Welcome to the official website of <strong>Sree Raghavendra Hardware, Plywoods & Doors</strong> (“we,” “us,” “our,” or “the Company”). By accessing and using this website, you agree to the following Terms & Conditions.
          </p>

          <div className="policy-section" id="about">
            <h3>{sections[0].icon} About Our Website</h3>
            <p>
              This website is created to provide information about Sree Raghavendra Hardware, Plywoods & Doors, our products, collections, services, business details, and contact information. Our website is an informational and enquiry platform only. We do not sell products or process purchases directly through this website. All product purchases, quotations, payments, orders, and other transactions are handled directly through our physical store and offline business channels.
            </p>
          </div>

          <div className="policy-section" id="products">
            <h3>{sections[1].icon} Our Products</h3>
            <p>
              Our product range includes:
            </p>
            <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
              <li>Hardware</li>
              <li>Plywood</li>
              <li>Wooden Doors</li>
              <li>PVC Doors</li>
              <li>Door Accessories</li>
              <li>Door Fittings</li>
              <li>Other related hardware and wood products</li>
            </ul>
            <p>
              Product images, colours, textures, designs, sizes, finishes, and specifications displayed on the website are for reference purposes only. Actual products may vary due to photography, lighting, screen settings, manufacturing variations, or product updates. Product availability and specifications may change without prior notice.
            </p>
          </div>

          <div className="policy-section" id="enquiries">
            <h3>{sections[2].icon} Product Enquiries</h3>
            <p>
              Customers may contact us through the phone number, enquiry forms, WhatsApp, or other contact options available on the website. Submitting an enquiry does not constitute an order, purchase, reservation, or confirmation of product availability. Our team may contact you to discuss product requirements, availability, pricing, specifications, and other details.
            </p>
          </div>

          <div className="policy-section" id="purchases">
            <h3>{sections[3].icon} Offline Purchases</h3>
            <p>
              All purchases from Sree Raghavendra Hardware, Plywoods & Doors are completed through our physical store or authorized offline business channels. Final product selection, pricing, availability, payment terms, delivery arrangements, installation requirements, and other purchase conditions will be discussed and confirmed directly with the customer. Information displayed on this website should not be considered a final quotation or binding offer.
            </p>
          </div>

          <div className="policy-section" id="pricing">
            <h3>{sections[4].icon} Pricing & Availability</h3>
            <p>
              Any pricing, offers, discounts, product specifications, or availability information displayed on the website is for general reference only. Final pricing and availability will be confirmed directly by our team at the time of purchase. Applicable taxes, delivery charges, installation charges, or other applicable costs will be communicated separately.
            </p>
          </div>

          <div className="policy-section" id="delivery">
            <h3>{sections[5].icon} Delivery & Installation</h3>
            <p>
              Where delivery or installation services are available, the applicable terms, charges, and timelines will be discussed directly with the customer. Delivery and installation timelines may vary depending on product availability, quantity, location, transportation, customization, and other circumstances.
            </p>
          </div>

          <div className="policy-section" id="warranty">
            <h3>{sections[6].icon} Product Warranty</h3>
            <p>
              Where applicable, product warranties will be subject to the warranty terms and conditions provided by the respective manufacturer or supplier. Warranty coverage may vary depending on the product and brand. Customers are advised to understand the applicable warranty terms at the time of purchase.
            </p>
          </div>

          <div className="policy-section" id="returns">
            <h3>{sections[7].icon} Returns, Exchanges & Cancellations</h3>
            <p>
              Returns, exchanges, cancellations, and replacement requests will be handled according to the applicable terms communicated at the time of purchase. Customized, made-to-order, cut-to-size, or specially processed products may not be eligible for return or cancellation unless otherwise agreed. For any product-related concern, customers should contact us directly.
            </p>
          </div>

          <div className="policy-section" id="content">
            <h3>{sections[8].icon} Website Content</h3>
            <p>
              We make reasonable efforts to ensure that the information provided on this website is accurate and up to date. However, we do not guarantee that all website content, product information, images, specifications, pricing, or availability will always be complete, accurate, current, or error-free. We reserve the right to update, modify, remove, or change website content at any time without prior notice.
            </p>
          </div>

          <div className="policy-section" id="intellectual">
            <h3>{sections[9].icon} Intellectual Property</h3>
            <p>
              All content on this website, including text, photographs, graphics, logos, designs, videos, product descriptions, and other materials, is owned by or licensed to Sree Raghavendra Hardware, Plywoods & Doors, unless otherwise stated. No website content may be copied, reproduced, modified, distributed, published, or commercially used without prior written permission.
            </p>
          </div>

          <div className="policy-section" id="links">
            <h3>{sections[10].icon} Third-Party Links</h3>
            <p>
              Our website may contain links to third-party websites, manufacturers, brands, social media platforms, or other external services. These links are provided for informational and convenience purposes. We are not responsible for the content, availability, privacy practices, or policies of third-party websites.
            </p>
          </div>

          <div className="policy-section" id="user">
            <h3>{sections[11].icon} User Responsibilities</h3>
            <p>
              When using our website, you agree not to:
            </p>
            <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
              <li>Use the website for unlawful purposes.</li>
              <li>Attempt unauthorized access to the website or its systems.</li>
              <li>Upload or transmit malicious software.</li>
              <li>Misuse enquiry or contact forms.</li>
              <li>Provide false or misleading information.</li>
              <li>Copy or reproduce website content without permission.</li>
              <li>Use website information for unauthorized commercial purposes.</li>
            </ul>
          </div>

          <div className="policy-section" id="liability">
            <h3>{sections[12].icon} Limitation of Liability</h3>
            <p>
              To the extent permitted by applicable law, Sree Raghavendra Hardware, Plywoods & Doors shall not be liable for any indirect, incidental, special, or consequential loss arising from the use of, or inability to use, this website. Product-related matters, including warranties, returns, replacements, and purchase-related concerns, will be handled according to the applicable purchase terms, manufacturer policies, and relevant laws.
            </p>
          </div>

          <div className="policy-section" id="changes">
            <h3>{sections[13].icon} Changes to These Terms</h3>
            <p>
              We may update these Terms & Conditions from time to time. Any changes will be published on this page with an updated “Last Updated” date.
            </p>
          </div>

          <div className="policy-section" id="law">
            <h3>{sections[14].icon} Governing Law</h3>
            <p>
              These Terms & Conditions shall be governed by and interpreted in accordance with the applicable laws of India. Any disputes shall be subject to the jurisdiction of the appropriate courts having jurisdiction over the business location of Sree Raghavendra Hardware, Plywoods & Doors.
            </p>
          </div>

          <div className="policy-section" id="contact" style={{ background: "#f8fafc", borderRadius: "12px", padding: "30px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ border: "none", padding: "0" }}>{sections[15].icon} Contact Us</h3>
            <p style={{ margin: "0 0 10px 0", color: "#0f172a", fontWeight: "700" }}>
              Sree Raghavendra Hardware, Plywoods & Doors
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Contact Person:</strong> Koushik Mattapalli
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Phone:</strong> <a href="tel:8374771149" style={{ color: brandColor, textDecoration: "none", fontWeight: "600" }}>83747 71149</a>
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Address:</strong> #11-35, Near Vaartha Paper, Kataari Nilayam, Morampudi, Rajamahendravaram, Andhra Pradesh.
            </p>
            <p style={{ margin: "0" }}>
              <strong>Website:</strong> <a href="http://www.raghavendradoorsandhardware.in" target="_blank" rel="noopener noreferrer" style={{ color: brandColor, textDecoration: "none", fontWeight: "600" }}>www.raghavendradoorsandhardware.in</a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsAndConditions;